import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, ChevronRightIcon, MinusIcon } from 'lucide-react';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { formatNzd, formatRtp, getCasinosForSlot, getProsCons, getProviderSlug, getSimilarSlots, getSlotByRank, getSlotBySlug } from '../utils/slots';
import { SlotThumb } from '../components/SlotThumb';
import { ScoreBadge } from '../components/ScoreBadge';
import { DemoFrame } from '../components/DemoFrame';
import { CasinoOfferList } from '../components/slot/CasinoOfferList';
import { MobileCtaBar } from '../components/slot/MobileCtaBar';
import { FaqSection } from '../components/FaqSection';
import { useSeo } from '../hooks/useSeo';
import { breadcrumbLd, faqLd, getSlotFaq, slotReviewLd } from '../utils/seo';
export function SlotDetail() {
  const {
    slug = ''
  } = useParams();
  const {
    showRealMoneyCtas
  } = useSiteSettings();
  const slot = getSlotBySlug(slug);
  const faq = slot ? getSlotFaq(slot) : [];
  useSeo(slot ? {
    title: `${slot.name} Review NZ – RTP ${formatRtp(slot.rtp)} & Gratis Demo | SlotGids`,
    description: `${slot.name} van ${slot.provider} getest voor Nieuw-Zeeland: RTP ${formatRtp(slot.rtp)}, ${slot.volatility.toLowerCase()} volatiliteit, max. winst ${slot.maxWin}. Speel gratis de demo. SlotGids Score ${slot.score}/10.`,
    path: `/slots/${slot.slug}`,
    ogType: 'article',
    jsonLd: [breadcrumbLd([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Top 30',
      path: '/'
    }, {
      name: slot.name,
      path: `/slots/${slot.slug}`
    }]), slotReviewLd(slot), faqLd(faq)]
  } : {
    title: 'Slot niet gevonden | SlotGids',
    description: 'Deze slot staat niet in de SlotGids Top 30.',
    path: `/slots/${slug}`,
    noindex: true
  });
  if (!slot) {
    return <main className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-6" data-id="element-507" data-mp="2911o68nkl09e">
        <h1 className="font-serif text-3xl font-semibold" data-id="element-508" data-mp="2dy1nm0pwz4rj">Deze slot staat niet in onze Top 30</h1>
        <p className="mt-3 text-muted" data-id="element-509" data-mp="2i059zke4ftrl">Misschien is hij uit de ranglijst gevallen of is de link veranderd.</p>
        <Link to="/#top-30" className="mt-6 inline-flex h-11 items-center rounded-lg bg-fern px-5 font-semibold text-white" data-id="element-510" data-mp="2n0kl05axkp6e">
          Bekijk de Top 30
        </Link>
      </main>;
  }
  const {
    pros,
    cons
  } = getProsCons(slot);
  const similar = getSimilarSlots(slot);
  const prev = getSlotByRank(slot.rank - 1);
  const next = getSlotByRank(slot.rank + 1);
  const casinoCount = getCasinosForSlot(slot).length;
  const stats = [{
    label: 'RTP',
    value: formatRtp(slot.rtp)
  }, {
    label: 'Volatiliteit',
    value: slot.volatility
  }, {
    label: 'Max. winst',
    value: slot.maxWin
  }, {
    label: 'Rollen',
    value: slot.reels
  }, {
    label: 'Inzet',
    value: `${formatNzd(slot.minBet)} – ${formatNzd(slot.maxBet)}`
  }, {
    label: 'Uitgebracht',
    value: String(slot.release)
  }];
  return <main className={showRealMoneyCtas ? 'pb-20 lg:pb-0' : ''} data-id="element-511" data-mp="3krzdtmdbmw3d">
      <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6" data-id="element-512" data-mp="3fto5jg2octn9">
        <nav aria-label="Kruimelpad" className="flex items-center gap-1 text-sm text-muted" data-id="element-513" data-mp="3fu7pf2bvxs3x">
          <Link to="/" className="hover:text-ink" data-id="element-514" data-mp="3btxa2z27dpce">Home</Link>
          <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-515" data-mp="cdh1h8gckudy" />
          <Link to="/#top-30" className="hover:text-ink" data-id="element-516" data-mp="7e4e8mlkdlau">Top 30</Link>
          <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-517" data-mp="7ewalzmzjn26" />
          <span className="truncate text-ink" aria-current="page" data-id="element-518" data-mp="2mcpmhlyxpnc">{slot.name}</span>
        </nav>

        <header className="mt-6 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-519" data-mp="3uq5clv3tzm40">
          <div className="flex items-center gap-5" data-id="element-520" data-mp="v4iayqf3g8br">
            <SlotThumb slot={slot} size="lg" data-id="element-521" data-mp="qd0b65ot4dsd" />
            <div data-id="element-522" data-mp="qc6yyp72ww2u">
              <p className="text-sm font-semibold text-oranje" data-id="element-523" data-mp="qbp4ny3z8rxk">#{slot.rank} in onze Top 30</p>
              <h1 className="mt-1 font-serif text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl" data-id="element-524" data-mp="ate58jsimuxk">
                {slot.name} review voor Nieuw-Zeeland
              </h1>
              <p className="mt-2 text-muted" data-id="element-525" data-mp="1vvf5100lqet">
                <Link to={`/providers/${getProviderSlug(slot.provider)}`} className="font-medium text-ink underline-offset-2 hover:underline" data-id="element-526" data-mp="5vmazj4eewq7">
                  {slot.provider}
                </Link>{' '}
                · {slot.mechanic}
              </p>
            </div>
          </div>
          <div className="shrink-0 self-start sm:self-center" data-id="element-527" data-mp="ksdeo4l3uulm">
            <ScoreBadge score={slot.score} size="lg" data-id="element-528" data-mp="ou8d91bnitvp" />
          </div>
        </header>
      </div>

      <div className="mx-auto mt-10 grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[minmax(0,1fr)_340px]" data-id="element-529" data-mp="1ckkqmrpo1ito">
        <div className="min-w-0" data-id="element-530" data-mp="1hbkmruqynwv4">
          <DemoFrame key={slot.slug} slot={slot} data-id="element-531" data-mp="1hcm065vg8vg0" />

          <dl className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-3" data-id="element-532" data-mp="3216j8txjnhd2">
            {stats.map(s => <div key={s.label} className="bg-surface px-5 py-4" data-id="element-533" data-mp="2x0ajmsk86hdg">
                <dt className="text-xs text-muted" data-id="element-534" data-mp="3bq83iwb4gkwu">{s.label}</dt>
                <dd className="mt-1 font-semibold tabular-nums" data-id="element-535" data-mp="36sgb816kews4">{s.value}</dd>
              </div>)}
          </dl>

          <article className="mt-12 space-y-12" data-id="element-536" data-mp="2j3nazsiwu2rp">
            <section aria-labelledby="verdict" data-id="element-537" data-mp="2j4f6l3cic0hz">
              <h2 id="verdict" className="font-serif text-2xl font-semibold" data-id="element-538" data-mp="2e4kh948driz1">Onze beoordeling</h2>
              <p className="mt-3 text-lg leading-relaxed" data-id="element-539" data-mp="2t1ta62dc0usv">{slot.verdict}</p>
              <p className="mt-3 leading-relaxed text-muted" data-id="element-540" data-mp="2o1yms8srmnck">
                Met een RTP van {formatRtp(slot.rtp)} en een {slot.volatility.toLowerCase()} volatiliteitsprofiel is {slot.name}{' '}
                {slot.volatility === 'Laag' || slot.volatility === 'Gemiddeld' ? 'geschikt voor wie langer wil spelen met een bescheiden budget.' : 'een slot voor spelers die bereid zijn langere droge periodes te accepteren voor een grotere uitschieter.'}{' '}
                De maximale winst bedraagt {slot.maxWin} je inzet.
              </p>
            </section>

            <section aria-labelledby="features" data-id="element-541" data-mp="pcgiuq98lgrr">
              <h2 id="features" className="font-serif text-2xl font-semibold" data-id="element-542" data-mp="pb5fusnm8d1p">Bonus features</h2>
              <ul className="mt-4 divide-y divide-line border-y border-line" data-id="element-543" data-mp="3nv3u8sy4d0pl">
                {slot.features.map(f => <li key={f} className="py-3 font-medium" data-id="element-544" data-mp="3sqatnokcvj8p">{f}</li>)}
              </ul>
            </section>

            <section aria-labelledby="proscons" className="grid gap-8 sm:grid-cols-2" data-id="element-545" data-mp="6dkp1g1aj4eg">
              <h2 id="proscons" className="sr-only" data-id="element-546" data-mp="353b3l56ivrl1">Plus- en minpunten</h2>
              <div data-id="element-547" data-mp="350ynkxkbrram">
                <h3 className="font-semibold text-fern" data-id="element-548" data-mp="3a25ter2wqgen">Pluspunten</h3>
                <ul className="mt-3 space-y-2.5" data-id="element-549" data-mp="3a01ihp6lch4v">
                  {pros.map(p => <li key={p} className="flex gap-2.5" data-id="element-550" data-mp="1r68g8sen1fjx">
                      <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-fern" aria-hidden="true" data-id="element-551" data-mp="1n56dw2pfcndq" />
                      <span data-id="element-552" data-mp="1i5k1twlc1d6i">{p}</span>
                    </li>)}
                </ul>
              </div>
              <div data-id="element-553" data-mp="1d6gw09xj32xh">
                <h3 className="font-semibold text-oranje-dark" data-id="element-554" data-mp="1d70fvw6qo1e5">Minpunten</h3>
                <ul className="mt-3 space-y-2.5" data-id="element-555" data-mp="188ytp2e0nlyg">
                  {cons.map(c => <li key={c} className="flex gap-2.5" data-id="element-556" data-mp="13ge309qepoz5">
                      <MinusIcon className="mt-0.5 h-4 w-4 shrink-0 text-oranje-dark" aria-hidden="true" data-id="element-557" data-mp="zfvaedgowevw" />
                      <span data-id="element-558" data-mp="uh0rlbiq2qbs">{c}</span>
                    </li>)}
                </ul>
              </div>
            </section>

            <section aria-labelledby="nz" data-id="element-559" data-mp="pgcty6zznb8l">
              <h2 id="nz" className="font-serif text-2xl font-semibold" data-id="element-560" data-mp="kikzp6bvfd18">
                Is {slot.name} beschikbaar in Nieuw-Zeeland?
              </h2>
              <p className="mt-3 leading-relaxed text-muted" data-id="element-561" data-mp="29e3fozrf6u81">
                Ja. {slot.provider} is actief op de Nieuw-Zeelandse markt en {slot.name} is{' '}
                {casinoCount > 0 ? `bij ${casinoCount} van onze partnercasino’s` : 'bij diverse casino’s'} te spelen
                in NZ$. Let op: vanaf 1 december 2026 mogen alleen operators met een aangevraagde NZ-vergunning spelers
                in Nieuw-Zeeland bedienen. Controleer daarom altijd de vergunning van het casino.
              </p>
            </section>

            <FaqSection id="slot-faq" title={`Veelgestelde vragen over ${slot.name}`} items={faq} data-id="element-562" data-mp="1uhceqm23lagb" />

            <section aria-labelledby="similar" data-id="element-563" data-mp="1gm9yp63je4ps">
              <h2 id="similar" className="font-serif text-2xl font-semibold" data-id="element-564" data-mp="1lo956oukphqy">Vergelijkbare slots</h2>
              <ul className="mt-4 grid gap-4 sm:grid-cols-3" data-id="element-565" data-mp="16qylis7s15k5">
                {similar.map(s => <li key={s.slug} data-id="element-566" data-mp="1bnysr6gd4ecu">
                    <Link to={`/slots/${s.slug}`} className="flex h-full items-center gap-3 rounded-xl border border-line bg-surface p-3 transition-colors duration-150 hover:border-ink/30" data-id="element-567" data-mp="1br4d68e1qs28">
                      <SlotThumb slot={s} size="sm" data-id="element-568" data-mp="122fl707y5kex" />
                      <div className="min-w-0" data-id="element-569" data-mp="11z1mq32elvcr">
                        <p className="truncate font-semibold" data-id="element-570" data-mp="x19vzcdhwfa5">{s.name}</p>
                        <p className="text-sm text-muted" data-id="element-571" data-mp="1b1kiy4bop9q1">#{s.rank} · {formatRtp(s.rtp)}</p>
                      </div>
                    </Link>
                  </li>)}
              </ul>
            </section>
          </article>

          <nav aria-label="Ranglijst" className="mt-12 grid grid-cols-2 gap-4 border-t border-line pt-6" data-id="element-572" data-mp="1krds6yoctcr4">
            {prev ? <Link to={`/slots/${prev.slug}`} className="group flex flex-col" data-id="element-573" data-mp="1uq32o6dx7uxc">
                <span className="flex items-center gap-1 text-sm text-muted" data-id="element-574" data-mp="1unz1vxbbjc1a">
                  <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-575" data-mp="1psjpl9sylo6z" /> #{prev.rank}
                </span>
                <span className="font-semibold group-hover:underline" data-id="element-576" data-mp="b2roo2vhrlnz">{prev.name}</span>
              </Link> : <span data-id="element-577" data-mp="68nihlu1vhny" />}
            {next && <Link to={`/slots/${next.slug}`} className="group flex flex-col items-end text-right" data-id="element-578" data-mp="1avqkrtenvjr">
                <span className="flex items-center gap-1 text-sm text-muted" data-id="element-579" data-mp="3tg931giq6krf">
                  #{next.rank} <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-580" data-mp="u0krl5647vld" />
                </span>
                <span className="font-semibold group-hover:underline" data-id="element-581" data-mp="p0zqop7dxd60">{next.name}</span>
              </Link>}
          </nav>
        </div>

        <aside className="lg:sticky lg:top-32 lg:self-start" data-id="element-582" data-mp="l1qxblg9bhaz">
          {showRealMoneyCtas ? <CasinoOfferList slot={slot} data-id="element-583" data-mp="g2ntg4c0jh4l" /> : <div className="rounded-2xl border border-line bg-surface p-5" data-id="element-584" data-mp="35qwsac7r24n0">
              <h2 className="font-serif text-xl font-semibold" data-id="element-585" data-mp="30q94s0iqc5xj">In het kort</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted" data-id="element-586" data-mp="1g8q6oot7gv7v">{slot.verdict}</p>
              <Link to="/#top-30" className="mt-4 inline-block text-sm font-semibold text-fern underline underline-offset-2" data-id="element-587" data-mp="1g6n9nktmy1tn">
                Terug naar de Top 30
              </Link>
            </div>}
          <p className="mt-4 text-xs leading-relaxed text-muted" data-id="element-588" data-mp="1u04owtfagsdv">
            Speel verantwoord. Hulp nodig? Bel de Gambling Helpline op 0800 654 655.
          </p>
        </aside>
      </div>

      {showRealMoneyCtas && <MobileCtaBar slot={slot} data-id="element-589" data-mp="23y2dxj4z2t7j" />}
    </main>;
}